/** Shared helpers for the Store Payouts admin page. */

export const PAYOUT_STATUS_LABELS = {
  AWAITING_DELIVERY: 'Awaiting delivery',
  HELD: 'Held',
  ON_HOLD: 'On hold',
  RELEASED: 'Released',
  FAILED: 'Failed',
  CANCELLED: 'Cancelled',
};

export const PAYOUT_STATUS_COLORS = {
  AWAITING_DELIVERY: 'default',
  HELD: 'info',
  ON_HOLD: 'warning',
  RELEASED: 'success',
  FAILED: 'error',
  CANCELLED: 'default',
};

export const FLAG_LABELS = {
  REFUNDED_AFTER_RELEASE: 'Refunded after payout',
  RELEASED_BEFORE_WINDOW_CLOSED: 'Paid before the window closed',
  COMMISSION_NOT_RETURNED: 'Commission not returned (settle manually)',
  RELEASE_UNCONFIRMED: 'Payout unconfirmed (check PayPal)',
};

export const FLAG_HINTS = {
  REFUNDED_AFTER_RELEASE: 'The buyer was refunded (fully or partly) for money that was also paid out to the store.',
  RELEASED_BEFORE_WINDOW_CLOSED:
    'Released before the normal payout date (return window + buffer), or while a return, dispute or hold was still open.',
  COMMISSION_NOT_RETURNED:
    'The buyer was refunded but the payment provider refused to send the commission back, so the '
    + 'platform fee on this refund has to be settled with the store manually.',
  RELEASE_UNCONFIRMED:
    'A payout was attempted and its result is unknown, but the buyer was refunded in full meanwhile. '
    + "Check the store's PayPal activity for a payout of this capture and settle it by hand if it went out.",
};

export const HOLD_REASON_LABELS = {
  RETURN: 'A return is open',
  DISPUTE: 'A payment dispute is open',
  ADMIN: 'Paused by an admin',
};

/** Status filter choices ('' = all). */
export const STATUS_FILTERS = [
  { value: '', label: 'All statuses' },
  ...Object.entries(PAYOUT_STATUS_LABELS).map(([value, label]) => ({ value, label })),
];

export const FLAG_FILTERS = [
  { value: '', label: 'Any flag' },
  ...Object.entries(FLAG_LABELS).map(([value, label]) => ({ value, label })),
];

export const formatMoney = (amount, currency) => {
  if (amount === null || amount === undefined || Number.isNaN(Number(amount))) return '—';
  return `${Number(amount).toFixed(2)} ${currency || ''}`.trim();
};

export const formatDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
};

/**
 * True when releasing now MAY be flagged RELEASED_BEFORE_WINDOW_CLOSED. Conservative: mirrors the
 * server's rule (an admin release is flagged whenever the payout is not due normally) from the
 * fields the list exposes. The server decides; a store-rejected return that is still disputable,
 * for instance, is not visible here, so the wording says "may".
 *
 * Not due normally = any hold open, no due date yet, due date still ahead (or unreadable), due date
 * capped at the hard deadline (the window can still be open then), or a status other than
 * HELD / FAILED (awaiting delivery, on hold).
 */
export const releasesEarly = (payout, now = Date.now()) => {
  if ((payout.holdReasons || []).length > 0) return true;
  if (payout.status !== 'HELD' && payout.status !== 'FAILED') return true;
  if (!payout.dueAt) return true;
  const due = new Date(payout.dueAt).getTime();
  if (Number.isNaN(due) || due > now) return true;
  if (payout.hardDeadlineAt) {
    const hard = new Date(payout.hardDeadlineAt).getTime();
    if (!Number.isNaN(hard) && due >= hard) return true;
  }
  return false;
};

export const ACTION_COPY = {
  release: {
    title: 'Release payout now',
    confirm: 'Release payout',
    verb: 'released',
    body: (p) => `Pay ${formatMoney(p.storeAmount, p.currencyCode)} out to ${p.storeName || 'the store'} now, `
      + 'ahead of the schedule. Money cannot be taken back from the store once paid out.',
  },
  hold: {
    title: 'Hold payout',
    confirm: 'Hold payout',
    verb: 'put on hold',
    body: (p) => `Pause the payout for order #${p.orderId}. The store will not be paid until an admin resumes it `
      + '(the hard deadline still applies: PayPal releases the money by itself).',
  },
  resume: {
    title: 'Resume payout',
    confirm: 'Resume payout',
    verb: 'resumed',
    body: (p) => `Lift the admin hold on order #${p.orderId}. Other holds (an open return or dispute) stay.`,
  },
  retry: {
    title: 'Retry failed payout',
    confirm: 'Retry payout',
    verb: 'retried',
    body: (p) => `Try again now to pay ${formatMoney(p.storeAmount, p.currencyCode)} out to `
      + `${p.storeName || 'the store'}.`,
  },
};

const UNKNOWN_OUTCOME_MESSAGE = 'The payout outcome is unknown: the store may or may not have been paid. '
  + 'Retrying is safe, the same request is reused and cannot pay out twice. Press Confirm again, or close this '
  + 'dialog to refresh the list and check the row: it may show Failed, or Cancelled with "Payout unconfirmed" '
  + 'if the order was refunded in the meantime (then check the store\'s PayPal activity).';

/**
 * Turns a failed release / hold / resume / retry call into an admin-readable message.
 *
 *  - 502 PAYOUT_RELEASE_FAILED: the provider definitively refused; nothing moved, the payout is FAILED
 *    (the job retries it later unless the order was cancelled or refunded meanwhile).
 *  - 502 PAYOUT_OUTCOME_UNKNOWN, and any other 5xx or no status (timeout, dropped connection): for
 *    release / retry the outcome is UNKNOWN, the store may or may not have been paid. Safe to retry:
 *    the same idempotency key is reused so it cannot pay twice. After a refresh the row may be FAILED,
 *    or CANCELLED + RELEASE_UNCONFIRMED when the order was refunded in the meantime.
 *  - 409 typed codes: the payout is not in a state that allows the action (list is stale), except
 *    PAYOUT_IN_PROGRESS which just needs a moment.
 *
 * Returns { message, retrySafe, stale, refresh } — `retrySafe` keeps the confirmation open so the
 * identical call can be repeated; `stale` means the list is out of date and the dialog can only be
 * closed; `refresh` means reload the list when the dialog closes.
 */
export const describePayoutActionError = (err, action = 'release') => {
  const status = err?.status;
  const code = err?.backendCode || '';
  const detail = err?.backendMessage || '';
  const money = action === 'release' || action === 'retry';

  if (code === 'PAYOUT_RELEASE_FAILED') {
    return {
      message: 'The payment provider refused the payout, so nothing was paid out'
        + `${detail ? ` (${detail})` : ''}. Close this dialog to refresh the list: the payout is marked Failed. `
        + 'The automatic job retries a failed payout once it is due (or at the payment provider\'s deadline), '
        + 'unless the order was cancelled or refunded; you can also Retry.',
      retrySafe: false,
      stale: false,
      refresh: true,
    };
  }
  if (money && (code === 'PAYOUT_OUTCOME_UNKNOWN' || status === undefined || status === null || status >= 500)) {
    return { message: UNKNOWN_OUTCOME_MESSAGE, retrySafe: true, stale: false, refresh: true };
  }
  if (status === undefined || status === null || status >= 500) {
    return {
      message: 'The server did not answer cleanly, so the change may or may not have been saved. Retrying is '
        + 'safe. Press Confirm again, or close this dialog to refresh the list.',
      retrySafe: true,
      stale: false,
      refresh: true,
    };
  }
  switch (code) {
    case 'PAYOUT_IN_PROGRESS':
      return {
        message: 'A payout for this order is being processed right now. Wait a moment and press Confirm again.',
        retrySafe: true,
        stale: false,
        refresh: true,
      };
    case 'PAYOUT_ALREADY_FINAL':
      return {
        message: 'This payout is already released or cancelled. Close this dialog to refresh the list.',
        retrySafe: false,
        stale: true,
        refresh: true,
      };
    case 'PAYOUT_NOT_RELEASABLE':
      return {
        message: 'This order is cancelled, failed or fully refunded, so there is nothing to pay out. '
          + 'Close this dialog to refresh the list.',
        retrySafe: false,
        stale: true,
        refresh: true,
      };
    case 'PAYOUT_NOT_FAILED':
      return {
        message: 'This payout has not failed, so there is nothing to retry. Close this dialog to refresh the list.',
        retrySafe: false,
        stale: true,
        refresh: true,
      };
    case 'PAYOUT_NOT_HELD_BY_ADMIN':
      return {
        message: 'There is no admin hold on this payout. Close this dialog to refresh the list.',
        retrySafe: false,
        stale: true,
        refresh: true,
      };
    case 'PAYOUT_NOT_FOUND':
      return { message: 'No payout was found for this order. Close this dialog to refresh the list.', retrySafe: false, stale: true, refresh: true };
    default:
      break;
  }
  if (status === 403) return { message: 'You do not have permission to manage payouts.', retrySafe: false, stale: false };
  if (status === 404) {
    return { message: 'No payout was found for this order. Close this dialog to refresh the list.', retrySafe: false, stale: true, refresh: true };
  }
  if (status === 409) {
    return {
      message: detail || 'The payout changed in the meantime. Close this dialog to refresh the list.',
      retrySafe: false,
      stale: true,
      refresh: true,
    };
  }
  return { message: detail || err?.message || 'The action could not be completed.', retrySafe: false, stale: false };
};

/** Digits only (no sign, exponent, separators or decimals). */
const INT_PATTERN = /^\d+$/;
/** A percentage with at most two decimals. */
const PERCENT_PATTERN = /^\d+(\.\d{1,2})?$/;

export const SETTINGS_LIMITS = { returnWindowDays: 365, bufferDays: 90, feeMax: 100 };

/**
 * Mirrors the server validation (400 otherwise): returnWindowDays integer 0..365, bufferDays
 * integer 0..90, refundAdminFeePercent 0..100 with at most 2 decimals. `values` are the raw input
 * strings. Returns { errors: {field: message}, valid, body } where `body` is the numeric payload
 * when valid.
 */
export const validatePayoutSettings = (values) => {
  const errors = {};
  const intField = (key, max, label) => {
    const raw = String(values[key] ?? '').trim();
    if (!INT_PATTERN.test(raw) || Number(raw) > max) {
      errors[key] = `${label} must be a whole number from 0 to ${max}.`;
    }
  };
  intField('returnWindowDays', SETTINGS_LIMITS.returnWindowDays, 'Return window');
  intField('bufferDays', SETTINGS_LIMITS.bufferDays, 'Buffer');
  const fee = String(values.refundAdminFeePercent ?? '').trim();
  if (!PERCENT_PATTERN.test(fee) || Number(fee) > SETTINGS_LIMITS.feeMax) {
    errors.refundAdminFeePercent = 'Refund admin fee must be a percentage from 0 to 100 with at most 2 decimals.';
  }
  const valid = Object.keys(errors).length === 0;
  return {
    errors,
    valid,
    body: valid
      ? {
        returnWindowDays: Number(values.returnWindowDays),
        bufferDays: Number(values.bufferDays),
        refundAdminFeePercent: Number(fee),
      }
      : null,
  };
};

/** Max store delivery time (gate G-08): 7 business days, plus the 2 business days before auto-deliver. */
export const MAX_DELIVERY_BUSINESS_DAYS = 7;
export const AUTO_DELIVER_GRACE_BUSINESS_DAYS = 2;

/** Business days to calendar days, rounded up so the check stays conservative (5 business days = 7 calendar). */
export const businessToCalendarDays = (businessDays) => Math.ceil((businessDays * 7) / 5);

/** A warning when return window + buffer + delivery gets close to (or past) the gateway's own release. */
export const gatewayWindowWarning = (body, gatewayMaxHoldDays) => {
  if (!body || gatewayMaxHoldDays === null || gatewayMaxHoldDays === undefined) return '';
  const deliveryBusiness = MAX_DELIVERY_BUSINESS_DAYS + AUTO_DELIVER_GRACE_BUSINESS_DAYS;
  const deliveryDays = businessToCalendarDays(deliveryBusiness);
  const total = body.returnWindowDays + body.bufferDays + deliveryDays;
  if (total < gatewayMaxHoldDays - 2) return '';
  return `Return window + buffer + up to ${deliveryBusiness} business days for delivery (${MAX_DELIVERY_BUSINESS_DAYS} `
    + `plus ${AUTO_DELIVER_GRACE_BUSINESS_DAYS} before it is marked delivered automatically, about ${deliveryDays} `
    + `calendar days) is ${total} days, close to or beyond the ${gatewayMaxHoldDays} days PayPal holds the money. `
    + 'PayPal would pay the store out by itself before the window closes.';
};

/** Admin error text for the load / settings calls. */
export const errorText = (err, fallback) => err?.backendMessage || err?.message || fallback;
